import { useEffect, useState, type ReactNode } from 'react'
import { Alert, AlertIcon, Box, Button, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useTicketAccess } from '../../hooks/tickets/useTicketAccess'
import { refreshTicketAccess } from '../../hooks/tickets/refreshTicketAccess'
import Loading from '../../pages/Loading'

export default function TicketAccessGuard({ children }: { children: ReactNode }) {
  const { isRoleResolved, canAccessTickets } = useTicketAccess()
  const client = useQueryClient()
  const { t } = useTranslation()
  const [attempt, setAttempt] = useState(0)
  const [checking, setChecking] = useState(true)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let mounted = true
    setChecking(true)
    setFailed(false)
    void refreshTicketAccess(client).catch(() => {
      if (mounted) setFailed(true)
    }).finally(() => { if (mounted) setChecking(false) })
    return () => { mounted = false }
  }, [client, attempt])
  if (checking || !isRoleResolved) return <Loading />
  if (failed || !canAccessTickets) return <Box p={5}>
    <Alert status={failed ? 'error' : 'info'} borderRadius="12px"><AlertIcon />
      <Text>{t(failed ? 'tickets.access.checkFailed' : 'tickets.access.noAccess')}</Text>
    </Alert>
    <Button mt={4} onClick={() => setAttempt(value => value + 1)}>{t('tickets.refreshAccess')}</Button>
  </Box>
  return <>{children}</>
}
